export interface NodeAnimator {
  animateNodePosition: (
    id: string | number,
    position: { x: number; y: number },
    duration?: number
  ) => Promise<void>;
  cancelAll: () => void;
}

export function createNodeAnimator(
  area: any,
  animationUpdateDuration = 100
): NodeAnimator {
  const activeAnimations = new Map<
    string,
    { animationId: number; cancel: () => void }
  >();

  const animateNodePosition = (
    id: string | number,
    position: { x: number; y: number },
    duration = animationUpdateDuration
  ): Promise<void> => {
    const nodeId = String(id);

    const existingAnimation = activeAnimations.get(nodeId);
    if (existingAnimation) {
      existingAnimation.cancel();
      activeAnimations.delete(nodeId);
    }

    const view = area.nodeViews.get(nodeId);
    if (!view) {
      return area.translate(nodeId, position).then(() => void 0);
    }

    const start = { ...view.position };
    const dx = position.x - start.x;
    const dy = position.y - start.y;

    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const startTime = performance.now();
      let animationId: number;
      let cancelled = false;

      const cancel = () => {
        cancelled = true;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };

      activeAnimations.set(nodeId, { animationId: 0, cancel });

      const step = (now: number) => {
        if (cancelled) {
          activeAnimations.delete(nodeId);
          reject(new Error('Animation cancelled'));
          return;
        }

        try {
          const elapsed = now - startTime;
          const t = Math.min(elapsed / duration, 1);
          const easedT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
          const x = start.x + dx * easedT;
          const y = start.y + dy * easedT;

          view
            .translate(x, y)
            .then(() => {
              if (cancelled) return;

              if (t < 1) {
                animationId = requestAnimationFrame(step);
                const animation = activeAnimations.get(nodeId);
                if (animation) {
                  animation.animationId = animationId;
                }
              } else {
                activeAnimations.delete(nodeId);
                resolve();
              }
            })
            .catch((error: unknown) => {
              console.error('Animation frame failed:', error);
              activeAnimations.delete(nodeId);
              reject(error as Error);
            });
        } catch (error) {
          console.error('Animation step failed:', error);
          activeAnimations.delete(nodeId);
          reject(error as Error);
        }
      };

      animationId = requestAnimationFrame(step);
      const animation = activeAnimations.get(nodeId);
      if (animation) {
        animation.animationId = animationId;
      }
    });
  };

  const cancelAll = () => {
    for (const [, anim] of activeAnimations) {
      anim.cancel();
    }
    activeAnimations.clear();
  };

  return { animateNodePosition, cancelAll };
}
